import { Pause, Play, UploadCloud } from "lucide-react";
import { useTakeoff } from "./store";
import { MONO, fmtSize } from "./theme";

const fmtSpeed = (bps) => {
  if (!bps) return null;
  const mbps = bps / 1048576;
  return mbps >= 1 ? `${mbps.toFixed(1)} MB/s` : `${Math.max(1, Math.round(bps / 1024))} KB/s`;
};

const phase = (u) => {
  if (u.status === "paused") return "Paused";
  if (u.status === "processing") return "Processing…";
  if (u.info?.verifying) return "Finishing…";
  if (u.status === "queued" || (!u.info?.done && !u.pct)) return "Queued";
  return null;
};

/** Per-file upload readout under the Plans header: progress bar, %, MB/s and
 *  time remaining for EVERY file in flight — a slow site link is obvious at a
 *  glance instead of hiding behind one header pill. Light-paper module styling. */
export default function UploadingRows({ uploads }) {
  const { pauseUpload, retryUpload } = useTakeoff();
  const live = uploads.filter((u) => !u.error && u.status !== "ready");
  if (!live.length) return null;
  return (
    <div className="border-b border-slate-100 px-3 py-2" data-testid="plans-uploading">
      <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        <UploadCloud className="h-3 w-3 animate-pulse text-[#ea580c]" /> Uploading ({live.length})
      </p>
      <ul className="space-y-1.5">
        {live.map((u) => {
          const ph = phase(u);
          const speed = fmtSpeed(u.info?.speedBps);
          const slow = u.info?.speedBps && u.info.speedBps < 512 * 1024 && !ph; // < 0.5 MB/s
          const done = u.info?.done || 0;
          const total = u.info?.total || u.file?.size || 0;
          return (
            <li key={u.key} className="rounded-md border border-slate-200 bg-white px-2 py-1.5" data-testid={`plan-uploading-${u.key}`}>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={u.name}>{u.name}</span>
                <span className={`${MONO} shrink-0 font-semibold ${slow ? "text-red-600" : u.status === "paused" ? "text-amber-700" : "text-slate-700"}`} data-testid={`plan-uploading-pct-${u.key}`}>
                  {u.status === "paused" ? `Paused · ${u.pct || 0}%` : ph || `${u.pct || 0}%`}
                </span>
                {/* Pause keeps the server session; Resume continues from the last confirmed part */}
                {u.status === "paused" ? (
                  <button type="button" onClick={() => retryUpload(u.key)} title="Resume from where it stopped"
                    className="inline-flex shrink-0 items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-800 hover:bg-emerald-100"
                    data-testid={`plan-upload-resume-${u.key}`}><Play className="h-3 w-3" /> Resume</button>
                ) : u.status === "uploading" && !ph ? (
                  <button type="button" onClick={() => pauseUpload(u.key)} title="Pause — finished parts are kept, resume any time"
                    className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100"
                    data-testid={`plan-upload-pause-${u.key}`}><Pause className="h-3 w-3" /> Pause</button>
                ) : null}
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-[width] duration-300 ${u.status === "paused" ? "bg-amber-400" : ph ? "animate-pulse bg-slate-400" : slow ? "bg-red-500" : "bg-[#ea580c]"}`} style={{ width: `${ph === "Queued" ? 0 : (u.pct || 0)}%` }} />
              </div>
              <div className={`${MONO} mt-1 flex flex-wrap items-center gap-x-2 text-[10px] text-slate-500`} data-testid={`plan-uploading-stats-${u.key}`}>
                {total ? <span>{fmtSize(done)} / {fmtSize(total)}</span> : null}
                {!ph && speed && <span className={slow ? "font-semibold text-red-600" : "text-slate-700"}>{speed}{slow ? " · slow link" : ""}</span>}
                {!ph && u.info?.etaText && <span>{u.info.etaText} left</span>}
                {u.info?.chunkCount ? <span>part {u.info.chunk || 0}/{u.info.chunkCount}</span> : null}
                {u.info?.retries ? <span className="text-amber-700">retry {u.info.retries}</span> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
